// ============================================================================
// PairsForm - Organisation Switcher
// ============================================================================
// - Tous les users multi-org : switch via memberships
// - SUPER_ADMIN Cecodev : peut aussi visiter des orgs hors-membership

import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Autocomplete,
  TextField,
  Chip,
  Typography,
  CircularProgress,
  Divider,
} from "@mui/material";
import { Building2, Check } from "lucide-react";
import { api } from "../lib/api";
import { cheminSansOrg, segmentOrg, urlOrg } from "../lib/urls";
import { toast } from "sonner";
import { useOrganisation } from "../contexts/OrganisationContext";
import { useAuth } from "../contexts/AuthContext";
import { decisionSelecteurOrg, libelleCasquettes, libelleTypeOrganisation, EXPLICATION_SELECTEUR } from "../lib/casquettes";
import { membershipParDefaut, type Membership } from "@cecodev/shared";

interface Organisation {
  id: string;
  nom: string;
  type: string;
  logoUrl?: string;
  actif: boolean;
  nbUsers?: number;
  slug?: string | null;
}

export function OrgSwitcher() {
  const { user } = useAuth();
  const {
    memberships,
    activeMembership,
    switchMembership,
    selectedOrganisationOverride,
    setSelectedOrganisationOverride,
    isLoading: isSwitching,
  } = useOrganisation();
  const [allOrganisations, setAllOrganisations] = useState<Organisation[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Org « maison » au retour d'immersion : la casquette prioritaire (lot D
  // 2026-10-27 — règle partagée serveur / client), plus le drapeau is_default.
  const membershipMaison = membershipParDefaut<Membership>(memberships);

  // « Vrai » super-admin : en immersion le token est réduit à PILOTE, mais les
  // memberships réels (renvoyés dans la session) gardent le rôle SUPER_ADMIN →
  // on garde ainsi le sélecteur visible pour sauter d'une org à l'autre / sortir.
  const isSuperAdmin =
    user?.roles.includes("SUPER_ADMIN") ||
    (user?.memberships?.some((m) => m.roles.includes("SUPER_ADMIN")) ?? false);

  // Pilote de la maison mère Cecodev (FX 2026-08-10) : peut aussi s'immerger,
  // mais uniquement dans le périmètre Cecodev (cecodev-inter + orgs clientes
  // Intra — règle serveur `canActAsPilote`, jamais un OF ni Standards). On lit
  // les memberships RÉELS pour que le sélecteur survive à l'immersion.
  const isPiloteCecodev =
    !isSuperAdmin &&
    (user?.memberships?.some(
      (m) => m.roles.includes("PILOTE") && m.organisationType === "CECODEV",
    ) ?? false);
  const peutVisiter = isSuperAdmin || isPiloteCecodev;

  // Charger toutes les orgs pour ceux qui peuvent visiter
  useEffect(() => {
    if (peutVisiter) {
      loadOrganisations();
    }
  }, [peutVisiter]);

  async function loadOrganisations() {
    try {
      setLoading(true);
      const response = await api.get("/organisations");
      setAllOrganisations(response.data.organisations || []);
    } catch (error) {
      console.error("Erreur chargement organisations:", error);
      toast.error("Erreur lors du chargement des organisations");
    } finally {
      setLoading(false);
    }
  }

  // Retour bêta 1 (lot D, décision FX Q1 « expliquer + masquer ») : le
  // sélecteur n'a de sens que pour qui PILOTE plusieurs organisations, ou peut
  // en visiter — un animateur ou un participant multi-org ne voit rien changer
  // en basculant (ses espaces personnels réunissent toutes ses casquettes, les
  // pages d'org portent l'org dans l'URL). Règles pures : lib/casquettes.ts.
  const decision = decisionSelecteurOrg({ memberships, activeMembershipId: activeMembership?.id, peutVisiter });
  if (!decision.afficher) {
    return null;
  }

  // Miroir client du périmètre serveur `isCecodevPerimeter` (types uniques :
  // CECODEV = org cecodev, INTER = org cecodev-inter) — un pilote ne voit que
  // les orgs où le switch-org aboutira, un SUPER_ADMIN voit tout.
  const orgsVisitables = isSuperAdmin
    ? allOrganisations
    : allOrganisations.filter((o) => ["CECODEV", "INTER", "CLIENT_INTRA"].includes(o.type));

  return (
    <Box sx={{ px: 2, py: 2, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
      {/* Section memberships (multi-org) */}
      {decision.afficherMemberships && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Building2 size={16} color="#1E4A43" />
            <Typography variant="caption" sx={{ fontWeight: 600, color: "#1E4A43" }}>
              Mes organisations
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, lineHeight: 1.35 }}>
            {EXPLICATION_SELECTEUR}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 1 }}>
            {decision.lignes.map((m) => (
              <Box
                key={m.id}
                onClick={() => {
                  if (m.id === activeMembership?.id) return;
                  // Lot 9.1 : sur le niveau organisation, l'URL commande — on
                  // change son segment et la garde de route bascule le jeton.
                  const reste = cheminSansOrg(location.pathname);
                  if (reste !== null) navigate(urlOrg(reste, segmentOrg(m.organisationSlug, m.organisationId)));
                  else switchMembership(m.id);
                  toast.success(`Basculement vers ${m.organisationNom}`);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1,
                  borderRadius: 1,
                  cursor: m.id === activeMembership?.id ? "default" : "pointer",
                  bgcolor: m.id === activeMembership?.id ? "rgba(75, 183, 165, 0.08)" : "transparent",
                  border: m.id === activeMembership?.id ? "1px solid rgba(75, 183, 165, 0.3)" : "1px solid transparent",
                  "&:hover": m.id !== activeMembership?.id ? { bgcolor: "rgba(0,0,0,0.04)" } : {},
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {m.organisationNom}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {libelleCasquettes(m.roles)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                  <Chip
                    label={libelleTypeOrganisation(m.organisationType)}
                    size="small"
                    sx={{
                      bgcolor: m.organisationType === "CECODEV" ? "#4BB7A5" : "#94a3b8",
                      color: "white",
                      fontSize: "0.65rem",
                      height: 20,
                    }}
                  />
                  {m.id === activeMembership?.id && <Check size={16} color="#4BB7A5" />}
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* Section immersion : visiter d'autres orgs (SUPER_ADMIN partout,
          pilote Cecodev sur le périmètre maison mère) */}
      {peutVisiter && (
        <>
          {decision.afficherMemberships && <Divider sx={{ my: 1 }} />}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Building2 size={16} color="#E1512C" />
            <Typography variant="caption" sx={{ fontWeight: 600, color: "#E1512C" }}>
              {isSuperAdmin ? "SUPER ADMIN - Visiter une organisation" : "Visiter une organisation"}
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <Autocomplete
              value={selectedOrganisationOverride}
              onChange={(_event, newValue) => {
                // Lot 9.1 : sur le niveau organisation, l'URL porte l'org — la
                // garde de route demande la bascule (immersion ou retour maison).
                // `newValue` est typé par la `value` du contexte (id, nom, type) :
                // le slug se relit dans la liste chargée, pas sur la valeur.
                const reste = cheminSansOrg(location.pathname);
                const orgChoisie = newValue ? orgsVisitables.find((o) => o.id === newValue.id) : undefined;
                const cible = newValue
                  ? segmentOrg(orgChoisie?.slug, newValue.id)
                  : segmentOrg(membershipMaison?.organisationSlug, membershipMaison?.organisationId);
                if (reste !== null && cible) navigate(urlOrg(reste, cible));
                else setSelectedOrganisationOverride(newValue);
                if (newValue) {
                  toast.success(`Vue active : ${newValue.nom}`);
                } else {
                  toast.info("Retour à votre organisation");
                }
              }}
              options={orgsVisitables}
              getOptionLabel={(option) => option.nom}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Rechercher une organisation..."
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: "0.875rem",
                    },
                  }}
                />
              )}
              renderOption={({ key, ...props }, option) => (
                <Box
                  component="li"
                  key={key}
                  {...props}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.nom}
                    </Typography>
                    {(option as any).nbUsers !== undefined && (
                      <Typography variant="caption" color="text.secondary">
                        {(option as any).nbUsers} utilisateurs
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={libelleTypeOrganisation(option.type)}
                    size="small"
                    sx={{
                      bgcolor: option.type === "CECODEV" ? "#4BB7A5" : "#94a3b8",
                      color: "white",
                      fontSize: "0.65rem",
                      height: 20,
                    }}
                  />
                </Box>
              )}
              size="small"
              fullWidth
            />
          )}

          {selectedOrganisationOverride && (
            <Box sx={{ mt: 1, p: 1, bgcolor: "rgba(225, 81, 44, 0.05)", borderRadius: 1 }}>
              <Typography variant="caption" sx={{ color: "#E1512C", fontWeight: 600 }}>
                Vue active : {selectedOrganisationOverride.nom}
              </Typography>
            </Box>
          )}
        </>
      )}

      {isSwitching && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
          <CircularProgress size={16} />
        </Box>
      )}
    </Box>
  );
}
